import React, { useMemo } from 'react';
import { pick, pipe, prop } from 'ramda';
import { useGameFeaturesSelector } from 'js/store/hooks/useGameFeaturesSelector';
import { usePortletDispatch, usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { MultiSelect } from 'js/components/common/Fields/MultiSelect';
import { CATEGORY_TYPE } from 'js/components/main/Games/constants';
import { setFilterCategoriesAction } from './actions';
import { filterByEmptyGames, filterByFeatures, filterByVolatility, filterByProviders } from './filters';
import { useCategoryName } from '../hooks/useCategoryName';

export const Categories = () => {
    const features = useGameFeaturesSelector(prop('gameFeatures'));
    const {
        categories,
        filters: { selectedCategories, selectedProviders, selectedFeatures, selectedVolatility }
    } = usePortletSelector(pick(['categories', 'filters']));
    const { games, gamesConfiguration } = useAppSelector(pick(['games', 'gamesConfiguration']));
    const t = usePortletTranslations();
    const dispatch = usePortletDispatch();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const getFormattedCategoryName = useCategoryName();

    const filteredCategories = useMemo(
        () => pipe(
            filterByProviders(selectedProviders, gamesConfiguration),
            filterByFeatures(selectedFeatures, features),
            filterByVolatility(selectedVolatility, features),
            filterByEmptyGames
        )(categories),
        [features, categories, selectedFeatures, selectedProviders, gamesConfiguration, selectedVolatility]
    );
    const recommendedArr = ['recommended-all-dynamic-category-recommended-all', 'recommended-live-dynamic-category-recommended-live'];
    const options = categories
        .filter(({ cid }) => !(cid === CATEGORY_TYPE.FAVORITES || cid === CATEGORY_TYPE.RECENTLY
            || Boolean(recommendedArr.filter(name => name === cid).toString()) && !isLoggedIn))
        .map(({ cid: id, displayName }) => {
            const { gameCodes = [], mostPlayedGameCode } = filteredCategories.find(cat => cat.cid === id) || {};
            const { name } = games[mostPlayedGameCode] || {};

            return {
                id,
                label: getFormattedCategoryName({ cid: id, categoryName: displayName, gameName: name }),
                hint: `(${gameCodes.length})`
            };
        });

    const onChange = (selected) => {
        dispatch(setFilterCategoriesAction(selected));
    };

    return (
        <MultiSelect
            label={t('gamesCategories')}
            options={options}
            selected={selectedCategories}
            onChange={onChange}
        />
    );
};

export default Categories;
